import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-lua';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-sql';
import { parseEmojis } from '../utils/emojiParser';
import './Documentation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Documentation() {
  const [scripts, setScripts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scriptsResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API_URL}/api/scripts`),
        axios.get(`${API_URL}/api/documentation/categories`)
      ]);
      setScripts(scriptsResponse.data);
      setCategories(categoriesResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const getScriptCategories = (scriptId) => {
    return categories.filter(cat => cat.script_id === scriptId);
  };

  const buildCategoryTree = (scriptId, parentId = null) => {
    return categories
      .filter(cat => cat.script_id === scriptId && cat.parent_id === parentId)
      .sort((a, b) => a.order_index - b.order_index)
      .map(cat => ({
        ...cat,
        children: buildCategoryTree(scriptId, cat.id)
      }));
  };

  const renderCategoryTree = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const hasContent = category.content && category.content.trim().length > 0;
    
    return (
      <div key={category.id}>
        <button
          onClick={() => hasContent && setSelectedCategory(category)}
          className={`sidebar-item ${selectedCategory?.id === category.id ? 'active' : ''} ${!hasContent ? 'no-content' : ''}`}
          style={{ paddingLeft: `${16 + level * 16}px` }}
          disabled={!hasContent}
        >
          {hasChildren && <span className="tree-icon">📁</span>}
          {!hasChildren && <span className="tree-icon">📄</span>}
          {category.name}
        </button>
        {hasChildren && (
          <div className="tree-children">
            {category.children.map(child => renderCategoryTree(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const renderContent = (content, isCode) => {
    const parsedContent = parseEmojis(content);
    const codeId = `code-${selectedCategory?.id}`;
    
    if (isCode) {
      // Auto-detect language or default to javascript
      const language = 'javascript'; // You can add language detection logic here
      const highlighted = Prism.highlight(parsedContent, Prism.languages[language], language);
      
      return (
        <div className="code-block-wrapper">
          <button 
            className={`copy-button ${copiedCode === codeId ? 'copied' : ''}`}
            onClick={() => copyToClipboard(parsedContent, codeId)}
            title={copiedCode === codeId ? "Copied!" : "Copy code"}
          >
            {copiedCode === codeId ? '✓' : '📋'}
          </button>
          <pre className="code-block">
            <code 
              className={`language-${language}`}
              dangerouslySetInnerHTML={{ __html: highlighted }}
            />
          </pre>
        </div>
      );
    }
    
    return (
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const codeContent = String(children).replace(/\n$/, '');
            const mdCodeId = `md-code-${Math.random()}`;
            
            if (!inline && match) {
              const language = match[1];
              const highlighted = Prism.highlight(
                codeContent, 
                Prism.languages[language] || Prism.languages.javascript, 
                language
              );
              
              return (
                <div className="code-block-wrapper">
                  <button 
                    className={`copy-button ${copiedCode === mdCodeId ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(codeContent, mdCodeId)}
                    title={copiedCode === mdCodeId ? "Copied!" : "Copy code"}
                  >
                    {copiedCode === mdCodeId ? '✓' : '📋'}
                  </button>
                  <pre className="code-block">
                    <code 
                      className={className}
                      dangerouslySetInnerHTML={{ __html: highlighted }}
                      {...props}
                    />
                  </pre>
                </div>
              );
            }
            
            return <code className={className} {...props}>{children}</code>;
          }
        }}
      >
        {parsedContent}
      </ReactMarkdown>
    );
  };

  return (
    <div className="documentation-page page-container">
      <div className="container">
        <div className="page-header fade-in">
          <h1 className="page-title">Documentation</h1>
          <p className="page-subtitle">Comprehensive guides for all our scripts</p>
        </div>

        {loading ? (
          <div className="loading glass">Loading documentation...</div>
        ) : (
          <div className="documentation-layout">
            {/* Sidebar */}
            <aside className="doc-sidebar glass">
              <div className="sidebar-header">
                <h3>📚 Browse Documentation</h3>
              </div>

              {/* Script Selection */}
              <div className="sidebar-section">
                <div className="sidebar-section-title">Select Script</div>
                {scripts.map(script => {
                  const scriptCategories = getScriptCategories(script.id);
                  if (scriptCategories.length === 0) return null;
                  
                  return (
                    <button
                      key={script.id}
                      onClick={() => {
                        setSelectedScript(script);
                        setSelectedCategory(null);
                      }}
                      className={`sidebar-item ${selectedScript?.id === script.id ? 'active' : ''}`}
                    >
                      <span className="tree-icon">🎮</span>
                      {script.title}
                    </button>
                  );
                })}
              </div>

              {/* Categories Tree */}
              {selectedScript && (
                <>
                  <div className="sidebar-divider"></div>
                  <div className="sidebar-section">
                    <div className="sidebar-section-title">Categories</div>
                    {buildCategoryTree(selectedScript.id).length === 0 ? (
                      <p className="sidebar-empty">No categories available</p>
                    ) : (
                      buildCategoryTree(selectedScript.id).map(cat => renderCategoryTree(cat))
                    )}
                  </div>
                </>
              )}
            </aside>

            {/* Main Content */}
            <main className="doc-content glass">
              {!selectedScript ? (
                <div className="doc-placeholder">
                  <div className="placeholder-icon">📚</div>
                  <h2>Welcome to Documentation</h2>
                  <p>Select a script from the sidebar to view its documentation</p>
                </div>
              ) : !selectedCategory ? (
                <div className="doc-placeholder">
                  <div className="placeholder-icon">📖</div>
                  <h2>{selectedScript.title}</h2>
                  <p>Select a category from the tree to view its content</p>
                  {buildCategoryTree(selectedScript.id).length === 0 && (
                    <p className="placeholder-note">No categories have been created yet for this script.</p>
                  )}
                </div>
              ) : (
                <div className="doc-article fade-in">
                  <div className="doc-breadcrumbs">
                    <span onClick={() => setSelectedScript(null)} className="breadcrumb-item">Documentation</span>
                    <span className="breadcrumb-separator">/</span>
                    <span onClick={() => setSelectedCategory(null)} className="breadcrumb-item">{selectedScript.title}</span>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">{selectedCategory.name}</span>
                  </div>
                  
                  <h1 className="doc-title">{selectedCategory.name}</h1>
                  
                  <div className="doc-body">
                    {renderContent(selectedCategory.content, selectedCategory.is_code)}
                  </div>
                </div>
              )}
            </main>
          </div>
        )}
      </div>
    </div>
  );
}

export default Documentation;
